<template>
    <PageHeader :title="t('recharge.title')" />
    <div class="page-wrapper" style="padding-top: calc(50px * 1.1)!important;">
      <div class="recharge-container desktop-container">
          

          <div class="amount-section">
              <div class="section-title">
                <span class="title-text">{{ t('recharge.amount') }}</span>
                <span class="title-subtitle">{{ t('recharge.amountSubtitle') }}</span>
              </div>
              <div class="amount-input-container"><span class="currency-symbol">{{ appConfig.currency.symbol }}</span>
                  <input name="price" type="tel" class="amount-input" v-model="amount" placeholder="0.00">
              </div>
          </div>


          <div class="quick-amount-section">
              <div class="section-title"><span class="title-text">{{ t('recharge.quickSelect') }}</span></div>
              <div class="amount-grid">
                  <div class="amount-card" :class="{ 'active': selectedAmount === amount }" v-for="amount in quickAmounts" :key="amount" @click="selectAmount(amount)">
                      <div class="amount-value">{{ appConfig.currency.symbol }}{{ amount }}</div>
                      <div class="amount-badge" v-if="selectedAmount === amount">
                        <span class="badge-text">✓</span>
                      </div>
                  </div>
              </div>
          </div>

          <div class="bottom-safe-area"></div>

          <div class="bottom-action">
              <div class="recharge-button" @click="showPaymentPopup">
                  <div class="button-content">
                    <span class="button-text">{{ t('recharge.rechargeNow') }}</span>
                    <span class="button-amount" v-if="amount">{{ appConfig.currency.symbol }}{{ amount }}</span>
                  </div>
                  <div class="button-icon">→</div>
              </div>
          </div>


          <div class="popup-overlay" v-if="showPopup">
              <div class="payment-popup">
                  <div class="popup-header">
                      <div class="popup-handle"></div><span class="popup-title">{{ t('recharge.selectPayment') }}</span><span class="popup-subtitle">{{ t('recharge.selectPaymentSubtitle') }}</span></div>
                  <div class="payment-methods">
                      <div class="payment-item" :class="{ 'selected': selectedPayment === payment.id }" v-for="payment in paymentMethods" :key="payment.id" @click="selectPayment(payment.id)">
                          <div class="payment-info">
                              <div class="payment-details"><span class="payment-name">{{ payment.name }}</span>
                                  <div class="payment-meta"><span class="payment-range">{{ payment.range }}</span><span class="payment-tag" :class="payment.id === 'bank' ? 'bank-tag' : 'crypto-tag'">{{ payment.tag }}</span></div>
                              </div>
                          </div>
                          <div class="payment-check" v-if="selectedPayment === payment.id"><span class="check-icon">✓</span></div>
                      </div>
                  </div>
                  <div class="popup-actions">
                      <div class="action-button cancel" @click="hidePaymentPopup">{{ t('recharge.cancel') }}</div>
                      <div class="action-button confirm" @click="confirmPayment">{{ t('recharge.confirm') }}</div>
                  </div>
              </div>
          </div>

          <div class="popup-overlay" v-if="showPaymentInfo">
              <div class="payment-info-popup">
                  <div class="popup-header">
                      <div class="popup-handle"></div><span class="popup-title">{{ t('recharge.paymentInfo') }}</span></div>
                  <div class="payment-info-content">
                      <div class="qrcode-container" v-if="paymentInfo.qrcode">
                          <img :src="paymentInfo.qrcode" class="qrcode-image" alt="QR Code">
                      </div>
                      <div class="usercode-section">
                          <div class="usercode-label">{{ t('recharge.usercode') }}</div>
                          <div class="usercode-input-container">
                              <input type="text" :value="paymentInfo.usercode" readonly class="usercode-input">
                              <div class="copy-button" @click="copyUsercode">
                                  <span class="copy-icon">📋</span>
                              </div>
                          </div>
                      </div>
                      <div class="screenshot-section">
                          <div class="screenshot-label">{{ t('recharge.uploadScreenshot') }}</div>
                          <div class="screenshot-upload" @click="selectScreenshot">
                              <div class="upload-placeholder" v-if="!screenshot">
                                  <span class="upload-icon">📷</span>
                                  <span class="upload-text">{{ t('recharge.clickToUpload') }}</span>
                              </div>
                              <div class="upload-preview" v-else>
                                  <img :src="screenshot" class="preview-image" alt="Screenshot">
                                  <div class="remove-button" @click.stop="removeScreenshot">✕</div>
                              </div>
                              <input type="file" ref="screenshotInput" accept="image/*" style="display: none" @change="handleScreenshotChange">
                          </div>
                      </div>
                  </div>
                  <div class="popup-actions">
                      <div class="action-button cancel" @click="hidePaymentInfo">{{ t('recharge.cancel') }}</div>
                      <div class="action-button confirm" @click="submitRecharge" :class="{ 'button-disabled': submitLoading }">
                          <span v-if="!submitLoading">{{ t('recharge.submit') }}</span>
                          <span v-else>{{ t('recharge.submitting') }}</span>
                      </div>
                  </div>
              </div>
          </div>
      </div>
      
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { appConfig } from '@/config/app'
import { doRecharge, getServiceData, getRechargeList, rechargeVisit } from '@/api'
import PageHeader from '@/components/PageHeader.vue'
import QRCode from 'qrcode'

const router = useRouter()
const { t } = useI18n()

onMounted(() => {
  window.scrollTo(0, 0)
  fetchRechargeList()
})

const amount = ref('')
const selectedAmount = ref('')
const showPopup = ref(false)
const showPaymentInfo = ref(false)
const selectedPayment = ref('')
const submitLoading = ref(false)
const paymentMethods = ref([])
const paymentInfo = ref({
  usercode: '',
  qrcode: ''
})
const screenshot = ref('')

const fetchRechargeList = async () => {
  try {
    const response = await getRechargeList()
    if (response.data.code === 200) {
      paymentMethods.value = response.data.data.map(item => ({
        id: item.name2,
        name: item.name,
        range: t('recharge.paymentRange', {
          symbol: appConfig.currency.symbol,
          minAmount: item.min,
          maxAmount: item.max
        }),
        tag: item.name2 === 'bank' ? t('recharge.bank') : t('recharge.crypto'),
        address: item.address || '',
        qrcode: item.qrcode || ''
      }))
      if (paymentMethods.value.length > 0) {
        selectedPayment.value = paymentMethods.value[0].id
      }
    }
  } catch (error) {
    console.error('获取充值列表失败:', error)
  }
}

const quickAmounts = [
  '20.00',
  '30.00',
  '50.00',
  '100.00',
  '300.00',
  '500.00',
  '1000.00',
  '3000.00',
  '5000.00',
  '10000.00',
  '20000.00',
  '50000.00',
  '100000.00'
]

const selectAmount = (value) => {
  selectedAmount.value = value
  amount.value = value
}

const showPaymentPopup = () => {
  if (amount.value) {
    showPopup.value = true
  }
}

const hidePaymentPopup = () => {
  showPopup.value = false
}

const selectPayment = (id) => {
  selectedPayment.value = id
}

const hidePaymentInfo = () => {
  showPaymentInfo.value = false
  paymentInfo.value = {
    usercode: '',
    qrcode: ''
  }
  screenshot.value = ''
}

const copyUsercode = () => {
  if (paymentInfo.value.usercode) {
    navigator.clipboard.writeText(paymentInfo.value.usercode).then(() => {
      ElMessage.success(t('recharge.copySuccess'))
    }).catch(() => {
      ElMessage.error(t('recharge.copyFailed'))
    })
  }
}

const screenshotInput = ref(null)

const selectScreenshot = () => {
  screenshotInput.value?.click()
}

const handleScreenshotChange = (event) => {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      screenshot.value = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

const removeScreenshot = () => {
  screenshot.value = ''
  if (screenshotInput.value) {
    screenshotInput.value.value = ''
  }
}

const submitRecharge = async () => {
  if (submitLoading.value) return
  
  if (!screenshot.value) {
    ElMessage.warning(t('recharge.uploadScreenshotRequired'))
    return
  }

  submitLoading.value = true

  try {
    const response = await doRecharge({
      num: parseFloat(amount.value),
      type: selectedPayment.value,
      img: screenshot.value
    })

    if (response.data.code === 200) {
      ElMessage.success(t('recharge.submitSuccess'))
      showPaymentInfo.value = false
      paymentInfo.value = {
        usercode: '',
        qrcode: ''
      }
      screenshot.value = ''
      amount.value = ''
      selectedAmount.value = ''
    } else {
      ElMessage.error(response.data.msg)
    }
  } catch (error) {
    console.error('提交失败:', error)
    ElMessage.error(t('recharge.submitFailed'))
  } finally {
    submitLoading.value = false
  }
}

const confirmPayment = async () => {
  if (!amount.value || !selectedPayment.value) {
    ElMessage.error(t('recharge.selectPayment'))
    return
  }

  const selectedMethod = paymentMethods.value.find(method => method.id === selectedPayment.value)
  if (!selectedMethod) {
    ElMessage.error(t('recharge.selectPayment'))
    return
  }

  showPopup.value = false
  paymentInfo.value.usercode = selectedMethod.address || ''
  
  if (selectedMethod.address) {
    try {
      paymentInfo.value.qrcode = await QRCode.toDataURL(selectedMethod.address, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      })
    } catch (error) {
      console.error('生成二维码失败:', error)
      paymentInfo.value.qrcode = ''
    }
  } else {
    paymentInfo.value.qrcode = ''
  }

  try {
    await rechargeVisit({
      num: parseFloat(amount.value),
      type: selectedPayment.value
    })
  } catch (error) {
    console.error('记录充值访问失败:', error)
  }
  
  showPaymentInfo.value = true
}
</script>

<style scoped>
.recharge-container{
    min-height: 100vh;
    background: var(--bg-secondary);
    padding-bottom: 60px
}

.amount-section{
    padding: 20px 15px 15px;
    background: var(--card-bg);
    margin: 10px;
    border-radius: 12px;
    box-shadow: var(--shadow-card)
}

.section-title{
    text-align: center;
    margin-bottom: 15px
}

.section-title .title-text{
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 4px
}

.section-title .title-subtitle{
    display: block;
    font-size: 12px;
    color: var(--text-secondary)
}

.amount-input-container{
    position: relative;
    display: flex;
    align-items: center;
    background: var(--bg-secondary);
    border-radius: 8px;
    padding: 0 12px;
    border: 2px solid transparent;
    transition: all .3s ease
}

.amount-input-container:focus-within {
    border-color: #667eea;
    background: var(--card-bg);
    box-shadow: 0 0 0 3px #667eea1a
}

.amount-input-container .currency-symbol{
    font-size: 18px;
    font-weight: 600;
    color: #667eea;
    margin-right: 6px
}

.amount-input-container .amount-input{
    flex: 1;
    height: 50px;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    background: transparent;
    border: none;
    outline: none
}

.amount-input-container .amount-input::placeholder {
    color: var(--text-placeholder)
}

.quick-amount-section{
    padding: 15px
}

.quick-amount-section .section-title{
    margin-bottom: 12px
}

.quick-amount-section .section-title .title-text{
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary)
}

.amount-grid{
    display: grid;
    grid-template-columns: repeat(3,1fr);
    gap: 8px;
    width: 100%
}

.amount-card{
    position: relative;
    background: var(--card-bg);
    border-radius: 10px;
    padding: 16px 8px;
    text-align: center;
    border: 2px solid var(--border-color);
    transition: all .3s cubic-bezier(.25,.46,.45,.94);
    overflow: hidden;
    box-sizing: border-box;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-sm);
    cursor: pointer
}

.amount-card:hover {
    box-shadow: var(--shadow-card);
    transform: translateY(-1px)
}

.amount-card:active {
    transform: scale(.98)
}

.amount-card.active{
    border-color: #667eea;
    background: linear-gradient(135deg,#667eea,#764ba2);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px #667eea4d
}

.amount-card.active .amount-value{
    color: #fff
}

.amount-card .amount-value{
    font-size: 16px;
    font-weight: 700;
    color: var(--text-primary);
    transition: color .3s ease;
    line-height: 1.2;
    width: 100%;
    text-align: center;
    letter-spacing: .2px
}

.amount-card .amount-badge{
    position: absolute;
    top: -4px;
    right: -4px;
    width: 18px;
    height: 18px;
    background: #4caf50;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    box-shadow: 0 2px 6px #4caf504d
}

.amount-card .amount-badge .badge-text{
    font-size: 10px;
    color: #fff;
    font-weight: 700
}

.bottom-safe-area{
    height: 70px
}

.bottom-action{
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--card-bg);
    padding: 10px 15px 20px;
    border-top: 1px solid var(--border-color);
    z-index: 999
}

.recharge-button{
    width: 100%;
    height: 50px;
    background: linear-gradient(135deg,#667eea,#764ba2);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 15px;
    box-shadow: 0 4px 12px #667eea4d;
    transition: all .3s ease;
    cursor: pointer
}

.recharge-button:active {
    transform: scale(.98)
}

.recharge-button .button-content{
    flex: 1
}

.recharge-button .button-content .button-text{
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #fff
}

.recharge-button .button-content .button-amount{
    display: block;
    font-size: 12px;
    color: #fffc;
    margin-top: 2px
}

.recharge-button .button-icon{
    font-size: 16px;
    color: #fff;
    font-weight: 600
}

.popup-overlay{
    position: fixed;
    inset: 0;
    background: #00000080;
    z-index: 99999;
    display: flex;
    align-items: flex-end;
    justify-content: center
}

.payment-popup{
    background: var(--card-bg);
    border-radius: 12px 12px 0 0;
    padding: 0;
    max-height: 80vh;
    overflow: hidden;
    width: 100%;
    max-width: 500px
}

.popup-header{
    text-align: center;
    padding: 12px 15px 10px;
    border-bottom: 1px solid var(--border-color);
    position: relative
}

.popup-header .popup-handle{
    width: 30px;
    height: 3px;
    background: var(--border-color);
    border-radius: 1.5px;
    margin: 0 auto 10px
}

.popup-header .popup-title{
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 4px
}

.popup-header .popup-subtitle{
    font-size: 12px;
    color: var(--text-secondary)
}

.payment-methods{
    padding: 10px;
    max-height: 50vh;
    overflow-y: auto
}

.payment-item{
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    margin-bottom: 8px;
    background: var(--bg-secondary);
    border-radius: 8px;
    border: 2px solid transparent;
    transition: all .3s ease;
    cursor: pointer
}

.payment-item.selected{
    background: #667eea1a;
    border-color: #667eea
}

.payment-item:active {
    transform: scale(.98)
}

.payment-item .payment-info{
    display: flex;
    align-items: center;
    flex: 1
}

.payment-item .payment-icon-wrapper{
    position: relative;
    margin-right: 12px
}

.payment-item .payment-icon-wrapper .payment-icon{
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: cover
}

.payment-item .payment-icon-wrapper .payment-type-icon{
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700
}

.payment-item .payment-icon-wrapper .payment-type-icon.bank-icon{
    background: linear-gradient(135deg,#4caf50,#45a049);
    color: #fff
}

.payment-item .payment-icon-wrapper .payment-type-icon.crypto-icon{
    background: linear-gradient(135deg,#ff9800,#f57c00);
    color: #fff
}

.payment-item .payment-icon-wrapper .payment-type-icon.default-icon{
    background: linear-gradient(135deg,#2196f3,#1976d2);
    color: #fff
}

.payment-item .payment-details{
    flex: 1
}

.payment-item .payment-details .payment-name{
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 4px
}

.payment-item .payment-details .payment-meta{
    display: flex;
    flex-direction: column;
    gap: 4px
}

.payment-item .payment-details .payment-meta .payment-range{
    font-size: 11px;
    color: var(--text-secondary)
}

.payment-item .payment-details .payment-meta .payment-tag{
    display: inline-block;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
    align-self: flex-start
}

.payment-item .payment-details .payment-meta .payment-tag.bank-tag{
    background: #4caf501a;
    color: #4caf50;
    border: 1px solid rgba(76,175,80,.3)
}

.payment-item .payment-details .payment-meta .payment-tag.crypto-tag{
    background: #ff98001a;
    color: #ff9800;
    border: 1px solid rgba(255,152,0,.3)
}

.payment-item .payment-details .payment-meta .payment-tag.default-tag{
    background: #2196f31a;
    color: #2196f3;
    border: 1px solid rgba(33,150,243,.3)
}

.payment-item .payment-check{
    width: 20px;
    height: 20px;
    background: #667eea;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center
}

.payment-item .payment-check .check-icon{
    font-size: 12px;
    color: #fff;
    font-weight: 600
}

.popup-actions{
    display: flex;
    gap: 10px;
    padding: 10px 15px 20px;
    border-top: 1px solid var(--border-color)
}

.action-button{
    flex: 1;
    height: 44px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    transition: all .3s ease;
    cursor: pointer
}

.action-button.cancel{
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 2px solid var(--border-color)
}

.action-button.cancel:active {
    background: var(--bg-hover)
}

.action-button.confirm{
    background: linear-gradient(135deg,#667eea,#764ba2);
    color: #fff
}

.action-button.confirm:active {
    transform: scale(.98)
}

.payment-info-popup{
    background: var(--card-bg);
    border-radius: 12px 12px 0 0;
    padding: 0;
    max-height: 90vh;
    overflow: hidden;
    width: 100%;
    max-width: 500px
}

.payment-info-content{
    padding: 15px;
    max-height: 70vh;
    overflow-y: auto
}

.qrcode-container{
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
    padding: 15px;
    background: var(--bg-secondary);
    border-radius: 12px
}

.qrcode-container .qrcode-image{
    width: 200px;
    height: 200px;
    object-fit: contain
}

.usercode-section{
    margin-bottom: 20px
}

.usercode-section .usercode-label{
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 10px
}

.usercode-input-container{
    display: flex;
    align-items: center;
    gap: 10px
}

.usercode-input-container .usercode-input{
    flex: 1;
    height: 44px;
    padding: 0 12px;
    background: var(--bg-secondary);
    border: 2px solid var(--border-color);
    border-radius: 8px;
    font-size: 14px;
    color: var(--text-primary);
    outline: none;
    cursor: not-allowed
}

.usercode-input-container .usercode-input[readonly]{
    background: var(--bg-secondary);
    color: var(--text-secondary)
}

.usercode-input-container .copy-button{
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg,#667eea,#764ba2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all .3s ease;
    flex-shrink: 0
}

.usercode-input-container .copy-button:active {
    transform: scale(.95)
}

.usercode-input-container .copy-button .copy-icon{
    font-size: 18px
}

.screenshot-section{
    margin-bottom: 20px
}

.screenshot-section .screenshot-label{
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 10px
}

.screenshot-upload{
    width: 100%;
    min-height: 150px;
    border: 2px dashed var(--border-color);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all .3s ease;
    background: var(--bg-secondary);
    position: relative;
    overflow: hidden
}

.screenshot-upload:hover {
    border-color: #667eea;
    background: #667eea0a
}

.screenshot-upload .upload-placeholder{
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px
}

.screenshot-upload .upload-placeholder .upload-icon{
    font-size: 32px
}

.screenshot-upload .upload-placeholder .upload-text{
    font-size: 14px;
    color: var(--text-secondary)
}

.screenshot-upload .upload-preview{
    width: 100%;
    height: 100%;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative
}

.screenshot-upload .upload-preview .preview-image{
    max-width: 100%;
    max-height: 200px;
    object-fit: contain
}

.screenshot-upload .upload-preview .remove-button{
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    background: rgba(0,0,0,.6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    transition: all .3s ease
}

.screenshot-upload .upload-preview .remove-button:hover {
    background: rgba(0,0,0,.8)
}

.button-disabled{
    opacity: .6;
    cursor: not-allowed
}

@media (max-width: 375px) {
    .amount-card{
        padding:12px 6px;
        min-height: 35px
    }

    .amount-card .amount-value{
        font-size: 14px
    }

    .amount-grid{
        gap: 6px
    }

    .amount-badge{
        width: 16px;
        height: 16px
    }

    .amount-badge .badge-text{
        font-size: 9px
    }

    .qrcode-container .qrcode-image{
        width: 160px;
        height: 160px
    }
}
</style>
